/* eslint-disable import/max-dependencies */
import React, { FC, useMemo, useState, useEffect } from 'react';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import AddIcon from '@mui/icons-material/Add';
import Button from '@mui/material/Button';
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import { useTranslate } from '@im/react-localization';
import Modal from 'src/components/Modal';
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';
import { useRoles } from 'src/provider/UserRoleProvider';
import { ProductGroup } from '../../hooks/useProductGroups';
import {
  Product,
  ProductSearchFilters,
  useProductSearchQuery,
  useProductsQueryParams,
  useRemoveProductsFromGroup,
} from '../../hooks/useProducts';
import SearchFilter from 'src/views/customerGroups/components/SearchFilter';
import AddProductsForm from './AddProducts/AddProductsForm';
import { RemoveProducts } from './RemoveProducts';
import upperFirst from 'lodash/upperFirst';
import ReusableTable, {
  GridSelectionModel,
} from 'src/views/customerGroups/CustomerGroupDetails/members/util/ReusableTable';

function useRemoveProducts(id: number) {
  const [productsToRemove, setProductsToRemove] = useState<
    number[] | undefined
  >(undefined);

  const mutation = useRemoveProductsFromGroup(id);

  const { isError, isLoading: isRemoving, isSuccess } = mutation;

  useEffect(() => {
    if (isSuccess || isError) {
      setProductsToRemove(undefined);
    }
  }, [isSuccess, isError]);

  const showRemoveConfirmation = !!productsToRemove?.length;

  function removeProducts(products: number[]) {
    setProductsToRemove(products);
  }

  function cancel() {
    setProductsToRemove(undefined);
  }

  return {
    cancel,
    isError,
    isRemoving,
    isSuccess,
    mutation,
    productsToRemove,
    removeProducts,
    showRemoveConfirmation,
  };
}

export interface ProductProps {
  productGroup: ProductGroup;
}

export interface ProductFilter {
  field: keyof Product;
  value: string;
}

const Products: FC<ProductProps> = ({ productGroup }) => {
  const translate = useTranslate();
  const { isCOGUser } = useRoles();
  const [tableSelection, setTableSelection] = useState<GridSelectionModel>([]);
  const [notification, setNotification] = useState('');
  const {
    cancel,
    isError,
    isSuccess,
    mutation,
    productsToRemove,
    removeProducts,
    showRemoveConfirmation,
  } = useRemoveProducts(productGroup.id);

  const { filter, pagination, searchQuery, setFilter } = useProductsQueryParams(
    {
      id: productGroup.id,
    }
  );

  const { data, error, isLoading } = useProductSearchQuery(searchQuery);

  const rows = useMemo(() => {
    if (error?.status === 404) {
      return [];
    }
    return data?.data || [];
  }, [data, error]);

  useEffect(() => {
    if (isSuccess) {
      setTableSelection([]);
      setNotification(
        translate('ProductGroups.removeProductsSuccessNotification')
      );
    }
    if (isError) {
      setNotification(
        translate('ProductGroups.removeProductsErrorNotification')
      );
    }
  }, [isSuccess, isError]); // eslint-disable-line react-hooks/exhaustive-deps

  const options = useMemo(
    () =>
      ProductSearchFilters.map((value) => ({
        label: translate(`ProductGroups.${upperFirst(value)}`),
        value,
      })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [translate, translate('ProductGroups.Name')]
  );

  const removeItem = (item: Product) => removeProducts([item.id]);

  const modelTriggerButton = ({
    onClick,
  }: {
    onClick(result: any): void;
  }): JSX.Element => (
    <Button
      onClick={onClick}
      startIcon={<AddIcon />}
      sx={{ display: isCOGUser ? 'inherit' : 'none' }}
      variant="newContained"
    >
      {translate('ProductGroups.AddProducts')}
    </Button>
  );
  return (
    <Box data-testid="product-group-products">
      <Stack direction="row" justifyContent="space-between">
        <SearchFilter filter={filter} onChange={setFilter} options={options} />
        <Stack direction="row" spacing={2}>
          <Button
            disabled={!tableSelection?.length}
            onClick={() => removeProducts(tableSelection.map(Number))}
            startIcon={<DeleteOutlinedIcon />}
            sx={{ display: isCOGUser ? 'inherit' : 'none' }}
            variant="newText"
          >
            {translate('ProductGroups.RemoveProducts')}
          </Button>
          <Modal
            notification={({ result }) =>
              translate('CustomerGroups.updateGroupSuccessNotification')
            }
            trigger={modelTriggerButton}
          >
            {({ onClose }) => (
              <AddProductsForm onClose={onClose} productGroup={productGroup} />
            )}
          </Modal>
        </Stack>
      </Stack>
      <ReusableTable<Product>
        checkboxSelection={isCOGUser}
        isProductGroups={true}
        loading={isLoading}
        onSelectionModelChange={setTableSelection}
        pagination={pagination}
        removeItem={removeItem}
        rows={rows}
        selectionModel={tableSelection}
        totalCount={data?.responsePreamble.totalCount ?? 0}
      />
      <Dialog
        aria-describedby={translate('CustomerGroups.removeCustomers')}
        aria-labelledby={translate('CustomerGroups.removeCustomers')}
        onClose={cancel}
        open={showRemoveConfirmation}
      >
        <DialogTitle>
          {translate('CustomerGroups.removeCustomersFromGroup')}
        </DialogTitle>
        <DialogContent>
          <RemoveProducts
            initialState={productsToRemove ?? []}
            mutation={mutation}
            onClose={cancel}
          />
        </DialogContent>
      </Dialog>
      <Snackbar
        anchorOrigin={{
          horizontal: 'center',
          vertical: 'top',
        }}
        autoHideDuration={6000}
        onClose={() => setNotification('')}
        open={!!notification}
      >
        <Alert severity={isSuccess ? 'success' : 'error'}>{notification}</Alert>
      </Snackbar>
    </Box>
  );
};

export default Products;
